import axios from "axios";
import { useState } from "react";
import {
  ChevronRight,
  Eye,
  EyeSlash,
  Lock,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";

import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  getErrorMessage,
} from "../settingsUtils";

const createPasswordForm = () => ({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
});

export default function PasswordSettings({ token }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const [form, setForm] = useState(createPasswordForm);
  const [message, setMessage] = useState(createEmptyMessage);

  const clearMessage = () => {
    setMessage(createEmptyMessage());
  };

  const closeEditor = () => {
    setForm(createPasswordForm());
    setShowPasswords(false);
    clearMessage();
    setIsEditing(false);
  };

  const toggleEditor = () => {
    if (isEditing) {
      closeEditor();
      return;
    }

    clearMessage();
    setIsEditing(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const setError = (error) => {
    setMessage({
      error,
      success: "",
    });
  };

  const savePassword = async () => {
    if (!form.currentPassword.trim()) {
      setError("Please enter your current password.");
      return;
    }

    if (!form.newPassword.trim()) {
      setError("Please enter your new password.");
      return;
    }

    if (!form.confirmPassword.trim()) {
      setError("Please confirm your new password.");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (form.currentPassword === form.newPassword) {
      setError(
        "Your new password must be different from your current password."
      );
      return;
    }

    try {
      setIsSaving(true);
      clearMessage();

      await axios.put(
        `${ApiConnectionString}/api/Account/Password`,
        {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setForm(createPasswordForm());
      setShowPasswords(false);
      setIsEditing(false);

      setMessage({
        error: "",
        success: "Password changed successfully.",
      });
    } catch (error) {
      console.error(error);

      setError(
        getErrorMessage(error, "Failed to change password.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="settings-list-item"
        onClick={toggleEditor}
        aria-expanded={isEditing}
      >
        <div>
          <Lock />

          <div>
            <strong>Change password</strong>
            <span>Update your password</span>
          </div>
        </div>

        <ChevronRight />
      </button>

      {message.success && !isEditing && (
        <div className="settings-list-message">
          <SectionMessage message={message} />
        </div>
      )}

      {isEditing && (
        <div className="settings-form settings-list-form">
          <SectionMessage message={message} />

          <div>
            <label htmlFor="settings-current-password">
              Current password
            </label>

            <input
              id="settings-current-password"
              type={showPasswords ? "text" : "password"}
              name="currentPassword"
              value={form.currentPassword}
              onChange={handleChange}
              autoComplete="current-password"
            />
          </div>

          <div>
            <label htmlFor="settings-new-password">
              New password
            </label>

            <input
              id="settings-new-password"
              type={showPasswords ? "text" : "password"}
              name="newPassword"
              value={form.newPassword}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </div>

          <div>
            <label htmlFor="settings-confirm-password">
              Confirm new password
            </label>

            <input
              id="settings-confirm-password"
              type={showPasswords ? "text" : "password"}
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </div>

          <button
            type="button"
            className="settings-password-visibility"
            onClick={() => setShowPasswords((current) => !current)}
          >
            {showPasswords ? <EyeSlash /> : <Eye />}

            {showPasswords
              ? "Hide passwords"
              : "Show passwords"}
          </button>

          <div className="settings-actions">
            <button
              type="button"
              onClick={savePassword}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save password"}
            </button>

            <button
              type="button"
              onClick={closeEditor}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}